import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { errorToast, successToast } from "../../toastNotification/toast.jsx";
import {
  loginRoute,
  landingPageRoute,
  verifyOtpRoute,
  authStage,
} from "../../constants/constants";
import {
  setIsValidOtp,
  selectIsValidOtp,
  setSignUpContact,
  setRemoveIsValidOtp,
  setRemoveSignUpContact,
} from "./authSlice.js";
import {
  contactSchema,
  otpSchema,
  confirmPasswordSchema,
} from "./../../validation/SignUpSchema";
import {
  useSendOtpMutation,
  useVerifyOtpMutation,
  useSignupMutation,
} from "./authApiSlice.js";
import { isEmail, isPhone } from "../../validation/contactValidation.js";

import "./style.css";
import { isSignUpContact, isValidOtp } from "../../constants/utils.js";

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const stage = queryParams.get("path") || authStage.role;
  const [errorMessage, setErrorMessage] = useState("");
  const [sendOtp, { isLoading }] = useSendOtpMutation();
  const [verifyOtp, { isLoading: isLoadingVerifyOtp }] = useVerifyOtpMutation();
  const [signUp, { isLoading: isLoadingSignup }] = useSignupMutation();
  const initialValues = {
    contact: "",
    otp: "",
    role: "",
    password: "",
    confirmPassword: "",
  };
  useEffect(() => {
    if (stage === authStage.password && !isValidOtp())
      navigate(landingPageRoute);
    if (stage === authStage.otp && !isSignUpContact() && !isValidOtp())
      navigate(landingPageRoute);
  }, []);
  const handleSignUp = async (values) => {
    let requestParams;
    if (isEmail(values.contact)) {
      requestParams = { email: values.contact };
    } else if (isPhone(values.contact)) {
      requestParams = { phone: values.contact };
    }

    try {
      switch (stage) {
        case authStage.role:
          navigate(`${landingPageRoute}?form=signup&path=${authStage.contact}`);
          break;

        case authStage.contact:
          if (values.role !== "cleaner" && values.role !== "customer")
            values.role = "customer";
          const responseContact = await sendOtp(requestParams).unwrap();
          successToast(responseContact.message);
          dispatch(setSignUpContact(values.contact));
          navigate(`${landingPageRoute}?form=signup&path=${authStage.otp}`);
          break;

        case authStage.otp:
          const responseOtp = await verifyOtp({
            ...requestParams,
            otp: values.otp,
          }).unwrap();
          successToast(responseOtp.message);
          dispatch(setIsValidOtp("valid"));
          navigate(
            `${landingPageRoute}?form=signup&path=${authStage.password}`
          );
          dispatch(setRemoveSignUpContact());
          break;

        case authStage.password:
          const responsePassword = await signUp({
            ...requestParams,
            role: values.role,
            password: values.password,
          }).unwrap();

          navigate(`${landingPageRoute}?form=login`);
          dispatch(setRemoveIsValidOtp());
          successToast("Successfully Signed Up!");
          break;

        default:
          dispatch(setIsValidOtp("inValid"));
          break;
      }
    } catch (error) {
      dispatch(setIsValidOtp("inValid"));
      errorToast(error.message);
      if (stage === authStage.password)
        navigate(`${landingPageRoute}?form=signup`);
    }
  };

  return (
    <div className="signup_main d-flex align-items-center">
      <div className="col-12 col-md-12 col-lg-12 d-flex justify-content-center align-items-center signup_sub_main ">
        <div className=" py-4 px-5 signup_box_main">
          {errorMessage && (
            <div className="d-flex justify-content-center">
              <p>{errorMessage}</p>
            </div>
          )}
          ​
          <h3 className="register_heading text-center mt-3">
            <span onClick={() => navigate(`${landingPageRoute}?form=login`)}>
              Login
            </span>
            &nbsp;/&nbsp;
            <span
              onClick={() => navigate(`${landingPageRoute}?form=signup`)}
              className="selected-auth-heading"
            >
              SignUp
            </span>
          </h3>
          <Formik
            onSubmit={handleSignUp}
            initialValues={initialValues}
            validationSchema={
              stage === authStage.contact
                ? contactSchema
                : stage === authStage.otp
                ? otpSchema
                : stage === authStage.password
                ? confirmPasswordSchema
                : ""
            }
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
              resetForm,
            }) => (
              <Form onSubmit={handleSubmit}>
                {stage === authStage.role && (
                  <Form.Group
                    controlId="formBasicRole"
                    className="mb-3 signup_label_input"
                  >
                    <Form.Select
                      className="input_signup mb-5 mt-2"
                      name="role"
                      value={values.role}
                      onChange={handleChange}
                    >
                      <option value="" disabled hidden>
                        Sign up as a...
                      </option>
                      <option value="cleaner" className="placeholders_signup">
                        Cleaner
                      </option>
                      <option value="customer" className="placeholders_signup">
                        Customer
                      </option>
                    </Form.Select>
                  </Form.Group>
                )}
                {stage === authStage.contact && (
                  <Form.Group
                    controlId="formBasicContact"
                    className="mb-3 signup_label_input"
                  >
                    <Form.Label>Enter email\phone number <span className="small"> (E.g. +447123123123)</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="contact"
                      value={values.contact}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      isInvalid={touched.contact && !!errors.contact}
                      disabled={isLoading}
                      className="input_signup mt-2"
                    />
                    {touched.contact && errors.contact && (
                      <Form.Control.Feedback type="invalid">
                        {errors.contact}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                )}
                {stage === authStage.otp && (
                  <Form.Group
                    controlId="formBasicOTP"
                    className="mb-3 signup_label_input"
                  >
                    <Form.Label>Enter OTP sent to your number</Form.Label>
                    <Form.Control
                      type="text"
                      name="otp"
                      minLength={6}
                      value={values.otp}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      isInvalid={touched.otp && !!errors.otp}
                      disabled={isLoading}
                      className="input_signup mt-2"
                    />
                    {touched.otp && errors.otp && (
                      <Form.Control.Feedback type="invalid">
                        {errors.otp}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                )}
                {stage === authStage.password && isValidOtp() && (
                  <Form.Group className="mb-3 signup_label_input">
                    <Form.Label>Enter Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={values.password}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      isInvalid={touched.password && !!errors.password}
                      disabled={isLoading}
                      className="input_signup mb-3 mt-2"
                    />
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      isInvalid={
                        touched.confirmPassword && !!errors.confirmPassword
                      }
                      disabled={isLoading}
                      className="input_signup"
                    />
                    {touched.password && errors.password && (
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                )}
                <div className="d-flex justify-content-center">
                  <button
                    className="signup_btn_next px-4 mt-4 mb-5"
                    type="submit"
                    disabled={isLoading}
                  >
                    {stage === authStage.password ? "Done" : "Next"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
